import { useEffect, useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Menu, X, ArrowRight, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRestaurantCart } from '@happyspace/sdk/restaurant';
import Logo from './Logo';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Menu', to: '/menu' },
  { label: 'About Us', to: '/about' },
  { label: 'Contact Us', to: '/contact' },
];

export default function Header({ onCartClick }: { onCartClick: () => void }) {
  const [open, setOpen] = useState(false);
  const { itemCount } = useRestaurantCart();

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `relative py-1 text-[13px] font-bold uppercase tracking-wide transition-colors ${
      isActive ? 'text-yellow' : 'text-white/85 hover:text-white'
    }`;

  return (
    <header
      data-hs-id="header"
      className="fixed top-0 left-0 right-0 z-50"
      style={{ background: 'var(--color-ink)' }}
    >
      <div className="max-w-screen-xl mx-auto px-5 md:px-8 flex items-center justify-between h-[72px] md:h-20">
        <Link to="/" className="min-w-0 shrink">
          <Logo tone="dark" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} end={link.to === '/'} className={navClass}>
              {({ isActive }) => (
                <>
                  {link.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-1 left-0 right-0 h-[3px] rounded-full"
                      style={{ background: 'var(--color-yellow)' }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <button
            onClick={onCartClick}
            aria-label={`Open cart, ${itemCount} item${itemCount === 1 ? '' : 's'}`}
            className="relative w-11 h-11 rounded-md flex items-center justify-center text-white hover:bg-white/10 transition-colors"
          >
            <ShoppingBag size={20} />
            {itemCount > 0 && (
              <motion.span
                key={itemCount}
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                className="absolute top-1.5 right-1 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-extrabold"
                style={{ background: 'var(--color-yellow)', color: 'var(--color-ink)' }}
              >
                {itemCount}
              </motion.span>
            )}
          </button>

          <Link to="/menu" className="hidden lg:inline-flex btn-yellow !px-5 !py-2.5">
            Order Now <ArrowRight size={15} />
          </Link>

          <button
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-md text-white hover:bg-white/10 transition-colors"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden border-t border-white/10"
            style={{ background: 'var(--color-ink)' }}
          >
            <nav className="flex flex-col px-5 py-4 gap-1">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === '/'}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `py-3 px-2 text-sm font-bold uppercase tracking-wide transition-colors ${
                      isActive ? 'text-yellow' : 'text-white/90'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Link to="/menu" onClick={() => setOpen(false)} className="btn-yellow mt-2">
                Order Now <ArrowRight size={15} />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
